import { Link } from 'react-router-dom'

const lora: React.CSSProperties = { fontFamily: "'Lora', serif" }
const nunito: React.CSSProperties = { fontFamily: "'Nunito', sans-serif" }

// Sunrise palette
const TERRACOTTA = '#c4714a'
const MEADOW = '#7aaa8a'
const CREAM = '#fdf8f2'
const BUTTER = '#f5e6d0'

const btnPrimary: React.CSSProperties = {
  background: TERRACOTTA,
  color: 'white',
  border: 'none',
}

const LandingPage = () => {
  return (
    <div className="min-h-screen overflow-x-hidden" style={{ background: CREAM, ...nunito }}>

      {/* Nav */}
      <nav className="px-6 py-5 flex items-center justify-between max-w-6xl mx-auto">
        <span className="font-bold tracking-tight text-lg" style={{ color: '#3d2a1e', ...lora }}>
          Backlog Explorer
        </span>
        <div className="flex items-center gap-3">
          <Link
            to="/auth?mode=login"
            className="text-sm font-semibold transition-colors"
            style={{ color: '#a68565' }}
          >
            Login
          </Link>
          <Link
            to="/auth?mode=signup"
            className="btn btn-sm rounded-full px-5"
            style={btnPrimary}
          >
            Sign Up
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 pt-12 pb-16 max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

          {/* Left: Text */}
          <div>
            <p
              className="text-xs uppercase tracking-widest font-bold mb-6"
              style={{ color: TERRACOTTA }}
            >
              ✦ Your library is full of good games
            </p>

            <h1
              className="text-5xl md:text-6xl font-bold leading-[1.1] mb-8"
              style={{ color: '#3d2a1e', ...lora }}
            >
              You probably<br />already own<br />
              <span style={{ color: TERRACOTTA }}>your next<br />favourite game.</span>
            </h1>

            <div
              className="text-lg leading-loose space-y-4 mb-10 border-l-2 pl-5"
              style={{ color: '#6b5344', borderColor: BUTTER }}
            >
              <p>You sit down to play. You have an hour.</p>
              <p>You open your library. Nothing feels right.</p>
              <p>You open the store instead.</p>
              <p className="font-semibold" style={{ color: '#3d2a1e', marginTop: '1.5rem' }}>
                Your hour is up. You bought something. You played nothing.
              </p>
            </div>

            <Link
              to="/auth?mode=signup"
              className="btn btn-lg rounded-full px-10"
              style={btnPrimary}
            >
              Find what to play tonight ✦
            </Link>
            <p className="text-xs mt-4" style={{ color: '#c4a882' }}>No credit card required.</p>
          </div>

          {/* Right: Laptop + floating card */}
          <div className="relative">

            {/* Subtle warm blob behind laptop */}
            <div
              className="absolute -top-8 -right-8 w-72 h-72 rounded-full -z-10 opacity-50"
              style={{ background: `radial-gradient(circle, ${BUTTER}, transparent)` }}
            />

            {/* Laptop mockup — slightly tilted */}
            <img
              src="/Macbook Pro 13_ Mockup.png"
              alt="Backlog Buddy showing game recommendations"
              style={{ display: 'block', width: '100%', transform: 'rotate(1.5deg)' }}
            />

            {/* Floating card — tilted opposite */}
            <div
              className="absolute bg-white rounded-2xl p-4 shadow-2xl w-48 md:w-52"
              style={{
                bottom: '18%',
                right: '-6%',
                transform: 'rotate(-3deg)',
                zIndex: 10,
                border: `1px solid ${BUTTER}`,
              }}
            >
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: TERRACOTTA }}>
                Before you buy
              </p>
              <p className="font-bold text-sm mb-3" style={{ color: '#3d2a1e', ...lora }}>
                The Adventures of Elliot
              </p>
              <div className="border-t pt-3 space-y-1.5" style={{ borderColor: BUTTER }}>
                <p className="text-xs mb-1.5 font-semibold" style={{ color: '#a68565' }}>You already own:</p>
                {["Zelda: Link's Awakening", 'Tunic'].map(game => (
                  <div key={game} className="flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#3d2a1e' }}>
                    <span style={{ color: MEADOW }}>✓</span> {game}
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* Arc-style bold statement */}
      <section className="py-20 px-6 text-center" style={{ background: CREAM }}>
        <div className="max-w-2xl mx-auto">
          <p className="text-3xl md:text-4xl font-bold leading-snug" style={{ color: '#3d2a1e', ...lora }}>
            Game stores help you buy games.<br />
            <span style={{ color: TERRACOTTA }}>Backlog Explorer helps you play them.</span>
          </p>
        </div>
      </section>

      {/* Wave */}
      <div className="overflow-hidden leading-none">
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-14">
          <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill={BUTTER} />
        </svg>
      </div>

      {/* Why gamers use it */}
      <section className="py-20 px-6" style={{ background: BUTTER }}>
        <div className="max-w-4xl mx-auto">
          <p
            className="text-center text-xs uppercase tracking-widest font-bold mb-3"
            style={{ color: TERRACOTTA }}
          >
            Why gamers use it
          </p>
          <h2
            className="text-3xl md:text-4xl font-bold text-center mb-16"
            style={{ color: '#3d2a1e', ...lora }}
          >
            Stop buying. Start playing.
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: '🎮',
                title: 'Stop buying games you already own.',
                body: 'Find the game you were about to pay for already sitting in your library.',
              },
              {
                icon: '🗺️',
                title: 'Break out of recommendation loops.',
                body: 'See connections between games no algorithm ever shows you.',
              },
              {
                icon: '✨',
                title: 'Actually play your collection.',
                body: 'Turn hundreds of unplayed games into a map worth exploring.',
              },
            ].map(({ icon, title, body }) => (
              <div key={title} className="text-center">
                <div className="text-3xl mb-4">{icon}</div>
                <h3 className="font-bold text-lg mb-2" style={{ color: '#3d2a1e', ...lora }}>{title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: '#6b5344' }}>{body}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link
              to="/auth?mode=signup"
              className="btn btn-lg rounded-full px-10"
              style={btnPrimary}
            >
              Try it free
            </Link>
            <p className="text-xs mt-4" style={{ color: '#a68565' }}>No credit card required.</p>
          </div>
        </div>
      </section>

      {/* Wave bottom */}
      <div className="overflow-hidden leading-none">
        <svg viewBox="0 0 1440 60" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" className="w-full h-14">
          <path d="M0,30 C360,0 1080,60 1440,30 L1440,0 L0,0 Z" fill={BUTTER} />
        </svg>
      </div>

      <footer className="py-8 px-6" style={{ background: CREAM }}>
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4 text-sm" style={{ color: '#a68565' }}>
          <span>© {new Date().getFullYear()} Backlog Explorer</span>
          <div className="flex gap-6">
            <Link to="/privacy" className="hover:opacity-70 transition-opacity">Privacy</Link>
            <Link to="/terms" className="hover:opacity-70 transition-opacity">Terms</Link>
          </div>
        </div>
      </footer>

    </div>
  )
}

export default LandingPage
